import React from "react";
import "./JobsView.scss";
import {Modal, Tabs} from "antd";
import {TabPosition} from "rc-tabs/lib/interface";
import {TabsType} from "antd/lib/tabs";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import {connect} from "react-redux";
import {SetExternalEditorUrlErrorAction} from "@data-force/app/interfaces/store/actions/editor";
import {setExternalEditorUrlError} from "@data-force/app/store/actions/editor";
import {Nullable} from "@data-force/app";
import StoreState from "../../interfaces/store/storeState";
import {ContentType, FilterType} from "../../enums/enums";
import CardList from "../../components/CardList/CardList";
import {
    destroyDetailsDrawer,
    getPhaseConfigList,
    setActiveJobType,
    setFilterType,
    setFiltersDrawerVisibility,
} from "../../store/actions/job";
import {
    DestroyDetailsDrawerAction,
    GetPhaseConfigListAction,
    SetActiveJobTypeAction,
    SetFilterTypeAction,
    SetFiltersDrawerVisibilityAction,
} from "../../interfaces/store/actions/job";

const {TabPane} = Tabs;

interface StateProps {
    translate: TranslateFunction
    activeJobType: string
    filter: string
    isMobile: boolean
    externalEditorError: Nullable<number>
}

interface DispatchProps {
    getPhaseConfigList(): GetPhaseConfigListAction

    setActiveJobType(payload: string): SetActiveJobTypeAction

    setFilterType(payload: string): SetFilterTypeAction

    destroyDetailsDrawer(): DestroyDetailsDrawerAction

    setFiltersDrawerVisibility(payload: boolean): SetFiltersDrawerVisibilityAction

    setExternalEditorUrlError(payload: Nullable<number>): SetExternalEditorUrlErrorAction
}

type Props = StateProps & DispatchProps;

class JobsView extends React.Component<Props> {
    private static readonly translationPath: string = "pages.JobsView.JobsView";

    constructor(props: Props) {
        super(props);

        this.onChangeHandler = this.onChangeHandler.bind(this);

        this.getTabPanes = this.getTabPanes.bind(this);
    }

    componentDidMount(): void {
        this.props.getPhaseConfigList();
    }

    componentWillUnmount(): void {
        this.props.destroyDetailsDrawer();
        this.props.setFiltersDrawerVisibility(false);
    }

    /**
     * On tab change event, set store's current active job type
     * this allows us to keep track of type of loaded job list
     * @param {string} currentTabType
     */
    onChangeHandler(currentTabType: string): void {
        const {AVAILABLE, IN_PROGRESS, COMPLETED}: typeof ContentType = ContentType;
        const {CLAIMABLE, ONGOING, DONE}: typeof FilterType = FilterType;

        switch (currentTabType) {
            case AVAILABLE:
                this.props.setFilterType(CLAIMABLE);
                break;
            case IN_PROGRESS:
                this.props.setFilterType(ONGOING);
                break;
            case COMPLETED:
                this.props.setFilterType(DONE);
                break;
            default:
                break;
        }
        this.props.setActiveJobType(currentTabType);
    }

    /**
     * Generate tab panes for each of jobType enum members
     * @return {React.ReactNode}
     */
    getTabPanes(): React.ReactNode {
        const {translate, activeJobType, isMobile}: Props = this.props;
        const jobTypes: string[] = Object.keys(ContentType);
        return jobTypes.map((jobType: string) => (
            <TabPane
                tab={translate(`${JobsView.translationPath}.${jobType}_TAB.text`)}
                key={jobType}
            >
                {activeJobType === jobType && (
                    <CardList isMobile={isMobile} key={activeJobType} isTest={false}/>
                )}
            </TabPane>
        ));
    }

    /**
     * Shows external editor error warning dialog
     */
    showExternalEditorError(): void {
        const {translationPath}: typeof JobsView = JobsView;
        const {error}: typeof Modal = Modal;
        const {translate}: Props = this.props;

        error({
            className: "df-external-editor-error",
            centered: true,
            title: translate(`${translationPath}.title.text`),
            content: translate(`${translationPath}.content.text`),
            okText: translate(`${translationPath}.close.text`),
            okCancel: false,
        });
    }

    render(): React.ReactNode {
        const {
            activeJobType, isMobile, translate, externalEditorError,
        }: Props = this.props;
        const tabPosition: TabPosition = isMobile ? "top" : "left";
        const tabType: TabsType = isMobile ? "line" : "card";

        if (externalEditorError && [403, 404].includes(externalEditorError)) {
            this.showExternalEditorError();
            this.props.setExternalEditorUrlError(null);
        }

        return (
            <div className="df-jobs-container">
                <Tabs
                    tabPosition={tabPosition}
                    type={tabType}
                    onChange={this.onChangeHandler}
                    activeKey={activeJobType}
                    moreIcon={(
                        <span aria-label={translate(`${JobsView.translationPath}.moreTabs.text`) as string}>
                            <Translate id={`${JobsView.translationPath}.moreTabsContent.text`}/>
                        </span>
                    )}
                >
                    {this.getTabPanes()}
                </Tabs>
            </div>
        );
    }
}

function mapStateToProps(state: StoreState): StateProps {
    return {
        translate: getTranslate(state.localize),
        activeJobType: state.job.activeJobType,
        filter: state.job.filter,
        isMobile: state.mobile.isMobile,
        externalEditorError: state.job.externalEditorError,
    };
}

const mapDispatchToProps: DispatchProps = {
    getPhaseConfigList,
    setActiveJobType,
    setFilterType,
    destroyDetailsDrawer,
    setFiltersDrawerVisibility,
    setExternalEditorUrlError,
};

export default connect(mapStateToProps, mapDispatchToProps)(JobsView);
